import React, { useState, useEffect, createRef } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import { makeStyles } from "@mui/styles";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Switch,
} from "@mui/material";
import { Link as LinkIcon, MoreVert, SmsFailed, TableChart, TableChartOutlined } from "@mui/icons-material";

/** Redux */
import { useSelector } from "react-redux";
import { getAdvancedStoryMaps, getShowIDs } from "../../../_appStore/Common";

/** Helpers */
import { useTranslation } from "react-i18next";
import useHelper from "../../../_common/useHelper";
import clsx from "clsx";

/** API */
import { getChartByIdFromAPI } from "../../../_APIs";

/** UI */
const AppTooltip = dynamic(() => import("../../../_common/AppTooltip"));
const Preloader = dynamic(() => import("../../../_common/Preloader"));
const ChartDialog = dynamic(() => import("./ChartDialog"));
const ChartDialogMenu = dynamic(() => import("./ChartDialogMenu"));
const ChartDialogExport = dynamic(() => import("./ChartDialogExport"));
const ChartDialogCustomize = dynamic(() => import("./ChartDialogCustomize"));
const ChartCardSubmenu = dynamic(() => import("./ChartCardSubmenu"));
const Certificate = dynamic(() => import("../../_Commons/Certificate/Certificate"));
const TabularView = dynamic(() => import("../../_Commons/TabularView/TabularView"));
const DependentGraphics = dynamic(() => import("./DependentGraphics"));
const Badania = dynamic(() => import("./Badania"));
/** - Charts */
const LineBarPieScatter = dynamic(() => import("../_charts/LineBarPieScatter/LineBarPieScatter"));
const LineBarPieScatterExt = dynamic(() => import("../_charts/LineBarPieScatter/LineBarPieScatter_ExtendLegend"));

const Map = dynamic(() => import("../_charts/Map/Map"));
const MapTools = dynamic(() => import("../../_Commons/MapTools/MapTools"));
const Pine = dynamic(() => import("../_charts/Pine/Pine"));
const WorldMap = dynamic(() => import("../_charts/WorldMap/WorldMap"));

const ChartsByType = (props) => {
    const { type, ...rest } = props;
    let Chart = null;

    switch (type.toUpperCase()) {
        case "LBPS":
            // Chart = <LineBarPieScatter {...rest} />;
            Chart = <LineBarPieScatterExt {...rest} />;
            break;
        case "MAP":
            Chart = <Map {...rest} />;
            break;
        case "PINE":
            Chart = <Pine {...rest} />;
            break;
        case "WM":
            Chart = <WorldMap {...rest} />;
            break;

        default:
            break;
    }
    return Chart;
};

const useStyles = makeStyles((theme) => ({
    Card: {
        backgroundColor: "transparent",
    },
    headerRoot: {
        padding: theme.spacing(0, 0), // <= #104 theme.spacing(1, 0),
    },
    headerTitle: {
        fontSize: "1.11111111rem",
        [theme.breakpoints.down("lg")]: {
            fontSize: "0.88888889rem",
        },
        fontWeight: 600,
    },
    contentRoot: {
        padding: theme.spacing(0, 0),
    },
    contentPaper: {
        minHeight: 530,
        //
        height: 530,
        // height: "100%",
        //
        position: "relative",
    },
    contentWMPaper: {
        // [theme.breakpoints.down("sm")]: {
        "@media (max-width: 399.95px)": {
            minHeight: 440,
            height: 440,
            position: "relative",
        },
        "@media (max-width: 320px)": {
            minHeight: 350,
            height: 350,
            position: "relative",
        },
    },
    actionsRoot: {
        padding: theme.spacing(1, 0),
        justifyContent: "space-between",
        [theme.breakpoints.down("lg")]: {
            // fontSize: "0.88888889rem",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 8,
            paddingBottom: 16,
        },
    },
    actionsBtn: {
        textTransform: "none",
        fontSize: "1.11111111rem",
        letterSpacing: "0.00938em",
        marginRight: theme.spacing(-1.5),
        [theme.breakpoints.down("lg")]: {
            marginLeft: "0!important",
            padding: 0,
            height: "unset",
            // fontSize: "0.88888889rem",
        },
        fontWeight: "normal",
        textDecoration: "underline",
    },
    actionsBtnB: {
        // textTransform: "none",
        fontSize: "0.7777778rem",
        fontWeight: "normal",
        // textDecoration: "underline",
    },
    sup: {
        fontSize: "0.555555556rem", //10,
        marginLeft: 4,
        color: "#AAA",
        fontWeight: 400,
    },
    cardChart: {
        // height: "auto",
        height: "100%",
        padding: 0,
        width: "100%",
        "&:last-child": {
            paddingBottom: 0,
        },
        display: "flex",
        flexGrow: 1,
        alignItems: "center",
        flexWrap: "wrap",
    },
    formControlLabel: {
        margin: 0,
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: "0.00938em",
        fontSize: "1rem",
        // color: "rgba(0, 0, 0, 0.5)",
        // fontSize: "max(0.9375vw, 0.75rem)",
        [theme.breakpoints.down("md")]: {
            fontSize: "1rem",
        },
    },
    formControlLabelEU: {
        color: theme.palette.primary.main,
        fontWeight: 500,
    },
    srOnly: {
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        border: 0,
    },
    zoomCard: {
        padding: theme.spacing(2),
        border: "none",
        display: "flex",
        width: "100%",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0, 0, 2, 1),
        },
    },
}));

const ChartCard = (props) => {
    const { area, chart } = props;
    // process.env.NODE_ENV === "development" && console.log("%c ChartCard: ", "background: #999; color:#FFF", props);

    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { translationFromJson } = useHelper();
    const cardRef = createRef(null);
    const cardDialogRef = createRef(null);

    /** States */
    const [CHART, setCHART] = useState(null);
    const [CHART_FONT, setCHART_FONT] = useState(16);
    const [CHILD_LOADING, setCHILD_LOADING] = useState(false);
    const [ERROR, setERROR] = useState(false);
    const [LOADING, setLOADING] = useState(false);
    const [READY, setREADY] = useState(false);
    const [SHOW_SUB_CHART, setSHOW_SUB_CHART] = useState({ open: false });
    const [SHOW_EUROPE, setSHOW_EUROPE] = useState(false);

    /** - dialogs */
    const [OPEN_CERTIFICATE, setOPEN_CERTIFICATE] = useState(false);
    const [OPEN_DIALOG, setOPEN_DIALOG] = useState({ open: false });
    const [OPEN_EXPORT, setOPEN_EXPORT] = useState(false);
    const [OPEN_CUSTOMIZE, setOPEN_CUSTOMIZE] = useState(false);
    const [VALUES, setVALUES] = useState({ name: "", value: "" });

    /** - redux */
    const advancedStoryMaps = useSelector(getAdvancedStoryMaps);
    const showIDs = useSelector(getShowIDs);
    /** /States */

    /** Effects */
    useEffect(() => {
        // if (!chart?.id || CHART) return;
        setLOADING(true);
        setERROR(false);

        getChartByIdFromAPI({ id_chart: chart.id, czy_jezyk_pl: i18n.language === "pl" })
            .then((response) => {
                // console.log({ response });
                if (response.error || response.message?.type === "Error") {
                    setERROR(response.message);
                } else {
                    /** dev */
                    // if (response.id === 1241) {
                    //     response.params.footerInfo = "2021";
                    // }
                    /** / dev */

                    setSHOW_EUROPE(!!response.eu);

                    setCHART(response);
                }
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setLOADING(false);
            });
    }, [chart]);

    /** Handlers */
    const handleOnChartItemClick = (props) => {
        const { data } = props;
        // process.env.NODE_ENV === "development" &&
        //     console.log(`%c handleOnChartItemClick   `, "background:#900; color:#FFF", props, chart, CHART);
        if (CHART.child && data && !SHOW_SUB_CHART.open) {
            setCHILD_LOADING(true);
            getChartByIdFromAPI({ id_chart: CHART.child.id, czy_jezyk_pl: i18n.language === "pl" })
                .then((response) => {
                    // console.log(response);
                    if (response.error || response.message?.type === "Error") {
                        setSHOW_SUB_CHART({
                            open: true,
                            error: response.message,
                        });
                    } else {
                        setSHOW_SUB_CHART({
                            open: true,
                            opts: data,
                            chart: response,
                            title: translationFromJson(response, "title"),
                        });
                    }
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    setCHILD_LOADING(false);
                });
        }
    };

    const handleCloseChartDialog = () => setSHOW_SUB_CHART({ open: false });

    const handleOnReady = () => setREADY(true);

    const handleChartFont = (value) => {
        setCHART_FONT(value);
    };

    const handleShowEurope = () => setSHOW_EUROPE(!SHOW_EUROPE);

    /** - dialogs */

    const handleOnCloseCertificate = ({ show }) => setOPEN_CERTIFICATE(show);

    const handleOnCloseDialog = ({ show }) => setOPEN_DIALOG({ ...OPEN_DIALOG, open: show });

    const handleOnCloseExport = ({ show }) => setOPEN_EXPORT(show);

    const handleOnClickTable = () => setOPEN_DIALOG({ open: true, type: "tabular" });

    const handleOnClickTableChild = ({ args }) => {
        // process.env.NODE_ENV === "development" &&
        //     console.log("%c handleOnClickTableChild: ", "background: #999; color:#FFF", { args });

        handleOnCloseDialog({ show: false });
        handleOnChartItemClick({ data: args });
    };

    const handleOnClickMeta = () => setOPEN_CERTIFICATE(true);

    const handleOnClickExport = () => setOPEN_EXPORT(true);

    const handleOnClickCustomize = () => setOPEN_CUSTOMIZE((prev) => !prev);

    const handleOnClickZoom = () =>
        setOPEN_DIALOG({ open: true, type: "zoom", title: translationFromJson(CHART, "title") || " " });

    const handleOnClickResearch = ({ title }) =>
        setOPEN_DIALOG({
            open: true,
            type: "research",
            title: title || `Informacje o badaniach zgodnie z PBSSP na rok 2022`,
            maxWidth: "md",
        });

    const handleOnClickExclamation = () =>
        setOPEN_DIALOG({
            open: true,
            type: "exclamation",
            title: t("Grafika zależna"),
            titleIcon: <SmsFailed fontSize={"small"} />,
            titleAlign: "left",
            maxWidth: "md",
        });

    /** / Handlers */

    return (
        <>
            {!!LOADING && <Preloader type={"linear"} style={{ marginTop: 64, marginBottom: 64 }} />}
            {!!ERROR && (
                <Alert variant="outlined" severity="error" sx={{ margin: 2, mb: 4 }}>
                    {translationFromJson(ERROR, "text")}
                </Alert>
            )}
            {!!CHART && (
                <>
                    <Card
                        ref={cardRef}
                        sx={{ minWidth: "100%", /*marginBottom: 2*/ marginBottom: -1, height: "100%" }}
                        elevation={0}
                        className={classes.Card}
                    >
                        <CardHeader
                            title={
                                <Box
                                    sx={{
                                        px: 0,
                                        display: "flex",
                                        alignItems: { xs: "flex-start", lg: "center" },
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexGrow: 1,
                                            flexDirection: { xs: "column", lg: "row" },
                                            justifyContent: "space-between",
                                            alignItems: { xs: "flex-start", lg: "center" },
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                fontWeight: "inherit",
                                                margin: "inherit",
                                            }}
                                        >
                                            <span>{translationFromJson(CHART, "title")}</span>
                                            {showIDs ? (
                                                <sup className={classes.sup}>
                                                    {"ID: "}
                                                    {CHART.id}
                                                </sup>
                                            ) : null}
                                        </Box>
                                        <Box>
                                            {CHART.type === "WM" && (
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <FormControl>
                                                        <FormLabel
                                                            id={`${CHART.id}-world-eu-radio-group-label`}
                                                            className={classes.srOnly}
                                                        >
                                                            {t("Typ mapy")}
                                                        </FormLabel>
                                                        <RadioGroup
                                                            aria-labelledby={`${CHART.id}-world-eu-radio-group-label`}
                                                            name={`${CHART.id}-world-eu-radio-group`}
                                                            value={SHOW_EUROPE}
                                                            onChange={handleShowEurope}
                                                            row
                                                            sx={{
                                                                flexDirection: "row",
                                                                flexWrap: { xs: "nowrap", lg: "wrap", xl: "nowrap" },
                                                            }}
                                                        >
                                                            <FormControlLabel
                                                                htmlFor={`${CHART.id}-world-radio`}
                                                                value={false}
                                                                control={
                                                                    <Radio
                                                                        inputProps={{ id: `${CHART.id}-world-radio` }}
                                                                    />
                                                                }
                                                                label={t("Świat")}
                                                                classes={{
                                                                    label: clsx(
                                                                        classes.formControlLabel,
                                                                        SHOW_EUROPE || classes.formControlLabelEU
                                                                    ),
                                                                }}
                                                            />
                                                            <FormControlLabel
                                                                htmlFor={`${CHART.id}-eu-radio`}
                                                                value={true}
                                                                control={
                                                                    <Radio
                                                                        inputProps={{ id: `${CHART.id}-eu-radio` }}
                                                                    />
                                                                }
                                                                label={t("Europa")}
                                                                classes={{
                                                                    label: clsx(
                                                                        classes.formControlLabel,
                                                                        SHOW_EUROPE && classes.formControlLabelEU
                                                                    ),
                                                                }}
                                                            />
                                                        </RadioGroup>
                                                    </FormControl>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: "flex" }}>
                                        {advancedStoryMaps && CHART.type === "MAP" /*|| CHART.type === "WM"*/ && (
                                            <MapTools chart={CHART} />
                                        )}
                                        <AppTooltip
                                            title={t("Pokaż dane z grafiki w postaci tabelarycznej")}
                                            arrow
                                            placement={"top-end"}
                                            type={"dark"}
                                            classes={{ tooltip: classes.tooltip }}
                                        >
                                            <IconButton
                                                size={"small"}
                                                color={"primary"}
                                                onClick={handleOnClickTable}
                                                aria-label={t(
                                                    "Pokaż dane z grafiki {{title}} w postaci tabelarycznej",
                                                    {
                                                        title: translationFromJson(CHART, "title") || " ",
                                                    }
                                                )}
                                                sx={{
                                                    p: { xs: 0, lg: 0.5 },
                                                    width: { xs: "auto", lg: 36 },
                                                    height: 36,
                                                    ml: { xs: 1, lg: 0 },
                                                }}
                                            >
                                                <TableChartOutlined sx={{ fontSize: "1.3333333rem" /*24,*/ }} />
                                            </IconButton>
                                        </AppTooltip>
                                        <ChartCardSubmenu
                                            keyId={`${CHART.id}`}
                                            widget={CHART}
                                            onClickTable={handleOnClickTable}
                                            onClickMeta={handleOnClickMeta}
                                            onClickExport={handleOnClickExport}
                                            onClickZoom={handleOnClickZoom}
                                            onClickCustomize={handleOnClickCustomize}
                                        />
                                    </Box>
                                </Box>
                            }
                            classes={{ root: classes.headerRoot, title: classes.headerTitle }}
                        />
                        <CardContent classes={{ root: classes.contentRoot }}>
                            <Paper
                                /*variant={"outlined"}*/ elevation={0}
                                className={clsx(classes.contentPaper, CHART.type === "WM" && classes.contentWMPaper)}
                            >
                                <>{!!READY || <Preloader type={"linear"} />}</>
                                <ChartsByType
                                    chart={CHART}
                                    type={CHART.type}
                                    onChartItemClick={handleOnChartItemClick}
                                    isChildOpened={SHOW_SUB_CHART?.open}
                                    onReady={handleOnReady}
                                    showEurope={SHOW_EUROPE}
                                    chartFont={CHART_FONT}
                                    onChartFont={(value) => handleChartFont(value)}
                                />
                            </Paper>
                        </CardContent>

                        {/* FooterInfo */}

                        {/* {CHART?.params?.footerInfo && (
                            <CardActions
                                sx={{
                                    pb: 0,
                                    pl: 0,
                                    pt: 0,
                                    fontSize: {
                                        xs: "1rem",
                                        sm: "1rem",
                                        md: "1rem",
                                        lg: "1rem",
                                    },
                                    fontWeight: 400,
                                    width: "100%",
                                    position: "relative",
                                    bottom: 0,
                                }}
                            >
                                {CHART?.params?.footerInfo}&nbsp;
                            </CardActions>
                        )} */}

                        {/* / FooterInfo */}

                        <CardActions classes={{ root: classes.actionsRoot }} sx={{ backgroundColor: "transparent" }}>
                            <Box component={"span"}>{CHART?.params?.footerInfo || ""}&nbsp;</Box>

                            {CHART.type === "WM" && (
                                <>
                                    <Box
                                        component={"a"}
                                        href={`/${i18n.language}/warunki-wykorzystania`}
                                        sx={{ color: "primary.main" }}
                                        className={classes.actionsBtn}
                                    >
                                        {t("© EuroGeographics dla granic administracyjnych")}
                                    </Box>
                                    {/* <span /> */}
                                </>
                            )}

                            <Button
                                size="small"
                                color={"primary"}
                                className={classes.actionsBtn}
                                href={
                                    CHART?.submenu?.meta?.indicator_id[0]
                                        ? `/${i18n.language}/baza-danych?id=${CHART?.submenu?.meta?.indicator_id?.join(
                                              ","
                                          )}`
                                        : undefined
                                }
                            >
                                {t("Przejdź do danych DBW")}
                            </Button>

                            {/* <Button
                                size="small"
                                color={"primary"}
                                className={classes.actionsBtnB}
                                startIcon={<LinkIcon />}
                                onClick={handleOnClickResearch}
                            >
                                {t("Badania")}
                            </Button> */}
                        </CardActions>
                    </Card>

                    <Certificate
                        indicatorId={CHART.submenu?.meta?.indicator_id}
                        item={CHART}
                        show={OPEN_CERTIFICATE}
                        onClose={(show) => handleOnCloseCertificate({ show })}
                        hideIcon={true}
                        target={"dialog"}
                        color={"primary"}
                    />

                    {/* Tabular view | Zoom */}
                    <ChartDialogMenu
                        open={OPEN_DIALOG.open || false}
                        onClose={(show) => handleOnCloseDialog({ show })}
                        title={OPEN_DIALOG.title}
                        titleIcon={OPEN_DIALOG.titleIcon}
                        titleAlign={OPEN_DIALOG.titleAlign}
                        color={"primary"}
                        maxWidth={OPEN_DIALOG.maxWidth}
                        noTitle={OPEN_DIALOG.type === "tabular"}
                    >
                        {OPEN_DIALOG.type === "tabular" && (
                            <TabularView
                                data={CHART.submenu?.table?.data}
                                color={"primary"}
                                child={CHART.child}
                                onChildClick={(args) => handleOnClickTableChild({ args })}
                                hidePreTitle={true}
                                chart={CHART}
                                area={area}
                                forStory={true}
                                // exportButtonTop={true}
                                onCloseDialog={(show) => handleOnCloseDialog({ show })}
                            />
                        )}

                        {OPEN_DIALOG.type === "research" && <Badania />}

                        {OPEN_DIALOG.type === "exclamation" && <DependentGraphics />}

                        {OPEN_DIALOG.type === "zoom" && (
                            <Card
                                variant={"outlined"}
                                // sx={{ p: 2, border: "none", display: "flex", width: "100%" }}
                                className={classes.zoomCard}
                            >
                                <Stack direction={"column"} sx={{ flexGrow: 1, width: "100%" }}>
                                    {!!VALUES?.name && (
                                        <CardContent className={classes.cardValues}>
                                            {JSON.stringify(VALUES)}
                                            <span className={classes.valuesValue}>{VALUES.value}</span>
                                            <span className={classes.valuesName}>{VALUES.name}</span>
                                        </CardContent>
                                    )}

                                    {!!CHART.type && (
                                        <CardContent classes={{ root: classes.cardChart }}>
                                            <ChartsByType
                                                chart={CHART}
                                                type={CHART.type}
                                                isZoomed={true}
                                                title={translationFromJson(CHART, "title") || " "}
                                                onTooltip={(args) => handleOnTooltip(args)}
                                                isInDialog={true}
                                                chartFont={CHART_FONT}
                                                onChartFont={(value) => handleChartFont(value)}
                                            />
                                        </CardContent>
                                    )}
                                    {!!CHART?.params?.footerInfo && (
                                        <CardActions
                                            sx={{
                                                pb: 0,
                                                pl: 0,
                                                pt: 0,
                                                fontSize: {
                                                    // xs: "1rem",
                                                    // sm: "1rem",
                                                    // md: "1rem",
                                                    xs: "0.77777778rem",
                                                    sm: "0.88888889rem",
                                                    md: "0.88888889rem",
                                                    lg: "1rem",
                                                },
                                                fontWeight: 400,
                                                width: "100%",
                                                position: "relative",
                                                bottom: 0,
                                            }}
                                        >
                                            {CHART?.params?.footerInfo}&nbsp;
                                        </CardActions>
                                    )}
                                </Stack>
                            </Card>
                        )}
                    </ChartDialogMenu>

                    {/* Export */}
                    <ChartDialogExport
                        open={OPEN_EXPORT || false}
                        onClose={(show) => handleOnCloseExport({ show })}
                        title={translationFromJson(CHART, "title") || " "}
                        color={"primary"}
                    >
                        <ChartsByType
                            chart={CHART}
                            type={CHART.type}
                            isZoomed={true}
                            title={translationFromJson(CHART, "title") || " "}
                            onTooltip={(args) => handleOnTooltip(args)}
                            chartFont={CHART_FONT}
                            onChartFont={(value) => handleChartFont(value)}
                        />
                    </ChartDialogExport>

                    {(!!SHOW_SUB_CHART?.open || CHILD_LOADING) && (
                        <ChartDialog
                            ref={cardDialogRef}
                            {...SHOW_SUB_CHART}
                            loading={CHILD_LOADING}
                            onClose={handleCloseChartDialog}
                        >
                            {!!SHOW_SUB_CHART?.chart && (
                                <Card
                                    variant={"outlined"}
                                    sx={{ p: 2, border: "none", display: "flex", width: "100%", height: "100%" }}
                                >
                                    {/* <Stack direction={"column"} sx={{ flexGrow: 1, width: "100%" }}> */}
                                    <CardContent classes={{ root: classes.cardChart }}>
                                        <ChartsByType
                                            type={SHOW_SUB_CHART.chart.type}
                                            chart={SHOW_SUB_CHART.chart}
                                            opts={SHOW_SUB_CHART.opts}
                                            isInDialog={true}
                                            isZoomed={true}
                                            chartFont={CHART_FONT}
                                            onChartFont={(value) => handleChartFont(value)}
                                        />
                                    </CardContent>
                                    {/* </Stack> */}
                                </Card>
                            )}
                        </ChartDialog>
                    )}

                    {!!OPEN_CUSTOMIZE && (
                        <ChartDialogCustomize
                            open={OPEN_CUSTOMIZE}
                            onClose={handleOnClickCustomize}
                            chart={CHART}
                            chartFont={CHART_FONT}
                            area={area}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default ChartCard;
